import { useContext, useEffect, useMemo, useState } from "react"
import { ChevronUp, Download, SquarePen, User, AlertTriangle, RotateCcw } from "lucide-react"
import { Button } from "@components/common/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@components/common/card"
import { BILLS_BY_BOQ_ID__LIST_ENDPOINT, FINANCIAL_SERVER_URL } from "@pma/utils/api-routes"
import axios from "axios"
import { parseCookies } from "nookies"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import { useQuery } from "@tanstack/react-query"
import { Avatar, AvatarFallback, AvatarImage } from "@components/common/avatar"
import useTranslator from "@pma/hooks/use-translator"
import { usePermissions } from "@pma/hooks/use-permissions"
import { DictType } from "@pma/types"
import BillRow from "./bill-row"
import PmsLoader from "@components/common/loader"
import { formatDistanceToNow } from "date-fns"
import { he } from "date-fns/locale"
import {
  cn,
  computeBillTotal, getAccumulatedTotal,
  exportToExcel,
  getUserFromId,
  getPreviouslyPaidTotal,
} from "@pma/utils/helpers"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import { formatCurrency } from "@pma/utils/utils"
import { useAuth } from "@pma/stores/auth-store"
import { USERS_PAGE_ROUTE } from "@pma/utils/page-routes"
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/common/tooltip"
import { Alert } from "@components/common/alert"
import { Input } from "@components/common/input"
import BillPrintTemplate from "./bill-print-template"
import { BillStatusBadge } from "./bill-status-badge"
import { Switch } from "../common/switch"
import BoQEdit from "./edit-bill"
import { UserRoleContext } from "@pma/contexts/UserRoleContext"
import ContractorBillingSummary from "./contractor-billing-summary"

interface BoQDetailsProps {
  boqId: string
  mainBoQDetails: any
  allBillsOnBoq?: any[]
}

export function ReturnedBills({ boqId, mainBoQDetails, allBillsOnBoq }: BoQDetailsProps) {
  const [expandedCards, setExpandedCards] = useState<{ [key: string]: boolean }>({})
  const { translate, locale } = useTranslator()
  const { authConfig } = useAuth()
  const { currentOrg } = usePermissions()

  const userRole = currentOrg
  const organizationUsers = authConfig?.organizationUsers

  const navigate = useNavigate()

  const isHebrew = locale === "he"
  const [searchParam] = useSearchParams()
  const newParams = new URLSearchParams(searchParam)
  const [showDeleted, setShowDeleted] = useState(false)
  const [selectBillId, setSelectedBillId] = useState<number>(0)
  const [showEditBillDialog, setEditBillDialog] = useState(false)
  const [searchKey, setSearchKey] = useState("")

  const { userRoleDetails } = useContext(UserRoleContext)

  const queryKey = searchParam.get("revalidate")

  const accessToken = parseCookies()[ACCESS_TOKEN]

  const toggleCard = (id: string | number) => {
    setExpandedCards((prev) => ({
      ...prev,
      [id]: !prev[id],
    }))
  }

  const getReturnedBills = async () => {
    const response = await axios.post(
      FINANCIAL_SERVER_URL + BILLS_BY_BOQ_ID__LIST_ENDPOINT,
      {
        boq_id: boqId,
        type: "for_constructor",
        show_deleted: showDeleted,
      },
      {
        headers: {
          Authorization: "Bearer " + accessToken,
          "Content-Type": "application/json",
        },
      },
    )
    return response.data
  }

  const {
    data,
    isLoading: isLoadingReturnedBills,
    refetch: refetchReturnedBills,
  } = useQuery({
    queryKey: ["returned", boqId, showDeleted],
    queryFn: getReturnedBills,
  })

  const sortBills = data?.items?.sort(
    (a: DictType, b: DictType) =>
      new Date(b?.created_at).getTime() - new Date(a?.created_at).getTime(),
  )
  const returnedBills = sortBills?.filter(
    (bill: DictType) => {
      if (!searchKey) return true
      const lowerSearch = searchKey.toLowerCase()
      return (
        bill?.number?.toString().includes(searchKey) ||
        bill?._created_by?.name?.toLowerCase().includes(lowerSearch) ||
        bill?._supervisor?.name?.toLowerCase().includes(lowerSearch)
      )
    },
  ) || []

  // Use mainBoQDetails.boq_items for accurate totals (fallback to first bill for compat)
  const boqItemsForSummary = useMemo(() => {
    if (mainBoQDetails?.boq_items?.length) {
      return mainBoQDetails.boq_items.filter((item: any) => item?.is_item)
    }
    return (sortBills?.[0]?._bill_items || []).map(
      (item: any) => item?._boq_item
    ).filter(Boolean)
  }, [mainBoQDetails?.boq_items, sortBills])

  useEffect(() => {
    if (queryKey === "bills") {
      refetchReturnedBills()
      newParams.delete("revalidate")
      navigate(`?${newParams.toString()}`)
    }
  }, [queryKey])

  return (
    <div className='space-y-6'>
      <Card>
        <CardHeader className='flex flex-col gap-4 sm:flex-row sm:justify-between'>
          <div className='w-full flex-1'>
            <CardTitle>{translate("New & Returned Bills")}</CardTitle>
            <CardDescription>{translate("View and review new returned bills")}</CardDescription>
          </div>

          <div className='flex items-center'>
            <div className='flex items-center gap-2'>
              <Switch
                checked={showDeleted}
                className='h-6 w-11'
                onCheckedChange={(checked) => setShowDeleted(checked)}
              />
              <h4 className='text-sm font-semibold'>{translate("Show Deleted Bills")}</h4>
            </div>
          </div>

          <div className='flex items-center gap-2 w-full sm:w-auto'>
            <Input
              type='text'
              placeholder={translate("Search Bills")}
              value={searchKey}
              onChange={(e) => setSearchKey(e.target.value)}
              className='w-full sm:w-64 lg:w-80'
            />
          </div>
        </CardHeader>
        <CardContent>
          <ContractorBillingSummary boqItems={boqItemsForSummary} />
          <div className='space-y-4'>
            {returnedBills?.length > 0 &&
              !isLoadingReturnedBills &&
              returnedBills?.map((bill: DictType, index: number) => (
                <Tooltip key={index}>
                  <TooltipTrigger asChild>
                    <Card key={index} className='overflow-hidden hover:shadow-md hover:border-primary/20 transition-all duration-200'>
                      <div className='p-1'>
                        <div className='flex cursor-pointer items-center justify-between'>
                          <div
                            className='flex h-full flex-1 flex-col p-2'
                            onClick={() => toggleCard(bill?.id)}
                          >
                            <div className='flex items-center gap-4'>
                              <h2 className='text-xl font-semibold'>#{bill?.number}</h2>-
                              <span className='text-lg font-semibold font-mono tabular-nums'>
                                {formatCurrency(computeBillTotal(bill, true), isHebrew)}
                              </span>
                            </div>
                            <div className='flex gap-2'>
                              <BillStatusBadge
                                status={bill?.status}
                                isApproved={bill?.is_approved}
                                isSigned={bill?.is_signed}
                                isDeclined={bill?.is_declined}
                                isReturnedToContractor={bill?.is_returned_to_contractor}
                              />

                              <small className='text-muted-foreground'>
                                ({bill?._bill_items?.filter((i: any) => i?.amount)?.length || 0}) {translate("Items")}
                              </small>
                            </div>
                            <div className='flex flex-wrap items-center gap-2 mt-1'>
                              <small className='text-xs text-blue-500 font-mono'>
                                {translate("total accumulated including this bill")}: {formatCurrency(
                                  getAccumulatedTotal(bill, allBillsOnBoq),
                                  isHebrew
                                )}
                              </small>
                            </div>
                          </div>
                          <div className='flex flex-col items-end justify-end'>
                            <div className='flex gap-3'>
                              {userRoleDetails?.constructor ||
                              userRoleDetails?.supervisor ||
                              userRoleDetails?.fin_manager ? (
                                <Button
                                  variant='default'
                                  size='sm'
                                  onClick={() => {
                                    setSelectedBillId(bill.id)
                                    setEditBillDialog(!showEditBillDialog)
                                  }}
                                >
                                  <SquarePen className='mx-1 h-4 w-4' />
                                  {translate("Edit Bill")}
                                </Button>
                              ) : null}

                              {bill?.is_returned_to_contractor && userRoleDetails?.constructor && (
                                <Button
                                  variant='outline'
                                  size='sm'
                                  className='border-orange-300 text-orange-700 hover:bg-orange-50'
                                  onClick={() => {
                                    setSelectedBillId(bill.id)
                                    setEditBillDialog(true)
                                  }}
                                >
                                  <RotateCcw className='mx-1 h-4 w-4' />
                                  {translate("edit_and_resubmit")}
                                </Button>
                              )}

                              {bill?.number && (
                                <BillPrintTemplate
                                  bill={bill}
                                  mainBoQDetails={mainBoQDetails}
                                  status='new'
                                />
                              )}

                              <Button
                                variant={"default"}
                                size={"sm"}
                                onClick={() => {
                                  const value = bill?._bill_items.filter(
                                    (item: DictType) => item?.amount,
                                  )
                                  exportToExcel(value, "bills", {}, isHebrew)
                                }}
                              >
                                <Download className={`mx-1 h-4 w-4`} />
                                {translate("Download")}
                              </Button>
                              <Button
                                variant='ghost'
                                size='sm'
                                className='mx-3'
                                onClick={() => toggleCard(bill?.id)}
                              >
                                <ChevronUp
                                  className={`h-6 w-6 transition-transform ${expandedCards[bill?.id] ? "" : "rotate-180"}`}
                                />
                              </Button>
                            </div>
                            <small className='text-muted-foreground'>
                              {translate("Created")}
                              {": "}
                              {formatDistanceToNow(new Date(bill?.created_at), {
                                addSuffix: true,
                                locale: isHebrew ? he : undefined,
                              })}
                            </small>
                          </div>
                        </div>

                        {expandedCards[bill?.id] && (
                          <div className='mt-4'>
                            {/* Supervisor Notes - Prominent Card */}
                            {(bill?.message?.text || bill?.returned?.reason) && (
                              <Card className='mb-4 border-amber-300 bg-amber-50'>
                                <CardContent className='p-3'>
                                  <div className='flex items-start gap-2'>
                                    <AlertTriangle className='mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600' />
                                    <div className='flex-1'>
                                      <h4 className='text-sm font-semibold text-amber-800'>
                                        {translate("supervisor_notes")}
                                      </h4>
                                      {bill?.message?.text && (
                                        <p className='mt-1 text-sm text-amber-900'>{bill.message.text}</p>
                                      )}
                                      {bill?.returned?.reason && (
                                        <p className='mt-1 text-sm text-amber-900'>
                                          <strong>{translate("Reason")}:</strong> {bill.returned.reason}
                                        </p>
                                      )}
                                      {bill?.message?.user_id && (
                                        <Link
                                          to={USERS_PAGE_ROUTE + `/${bill?.message?.user_id}`}
                                          className='mt-2 flex w-fit items-center gap-2 text-xs text-amber-700 hover:underline'
                                        >
                                          <Avatar className='h-5 w-5'>
                                            <AvatarImage
                                              src={
                                                getUserFromId(bill?.message?.user_id, organizationUsers)
                                                  .user_picture?.url || `${process.env.PUBLIC_URL}/usr.svg`
                                              }
                                              alt='Avatar'
                                            />
                                            <AvatarFallback className='text-[10px] uppercase'>
                                              {getUserFromId(bill?.message?.user_id, organizationUsers)?.first_name?.[0]}
                                              {getUserFromId(bill?.message?.user_id, organizationUsers)?.last_name?.[0]}
                                            </AvatarFallback>
                                          </Avatar>
                                          {getUserFromId(bill?.message?.user_id, organizationUsers)?.name}
                                        </Link>
                                      )}
                                    </div>
                                  </div>
                                </CardContent>
                              </Card>
                            )}

                            {bill?.returned?.reason || bill?.resubmission?.resubmitted_by ? (
                              <div className='flex w-full flex-col items-center justify-center gap-2'>
                                <Alert className='w-fit' variant={"destructive"}>
                                  {translate(
                                    bill?.resubmission?.resubmitted_by
                                      ? "Bill has been resubmitted by"
                                      : "Bill has been returned by",
                                  )}{" "}
                                  {bill?.resubmission?.resubmitted_by
                                    ? bill?.resubmission?._resubmitted_by?.name
                                    : bill?.returned?._returned_by?.name}
                                  ({translate(userRole?.role?.name)})
                                </Alert>
                              </div>
                            ) : (
                              <></>
                            )}

                            {/* Resubmission Context - Per-item previous values */}
                            {bill?.is_returned_to_contractor && bill?._bill_items?.some(
                              (item: any) => item?.approved_amount !== undefined && item?.approved_amount < item?.amount
                            ) && (
                              <Card className='mb-4 mt-2 border-blue-200 bg-blue-50'>
                                <CardContent className='p-3'>
                                  <h4 className='text-sm font-semibold text-blue-800 mb-2'>
                                    {translate("previously_requested")} / {translate("previously_approved")}
                                  </h4>
                                  <div className='space-y-1'>
                                    {bill._bill_items
                                      .filter((item: any) => item?.approved_amount !== undefined && item?.approved_amount < item?.amount)
                                      .map((item: any, idx: number) => (
                                        <div key={idx} className='flex items-center gap-3 text-xs text-blue-900 bg-blue-100/50 rounded px-2 py-1'>
                                          <span className='font-mono'>#{item?._boq_item?.number}</span>
                                          <span>{item?._boq_item?.description?.slice(0, 40)}</span>
                                          <span className='text-blue-600'>
                                            {translate("previously_requested")}: {item?.amount}
                                          </span>
                                          <span className='text-orange-600 font-medium'>
                                            {translate("previously_approved")}: {item?.approved_amount}
                                          </span>
                                        </div>
                                      ))}
                                  </div>
                                </CardContent>
                              </Card>
                            )}

                            <div className='flex flex-col gap-4 px-0 sm:px-2'>
                              <div className='flex w-full flex-col gap-4 md:flex-row md:justify-between'>
                                <div className='min-w-0 flex-1 gap-1'>
                                  <div className='flex flex-wrap items-center gap-2'>
                                    <strong>{translate("Requested Sum")}: </strong>{" "}
                                    <span className='text-lg font-semibold text-green-400'>
                                      {formatCurrency(computeBillTotal(bill, false), isHebrew)}
                                    </span>
                                  </div>
                                  <div className='flex flex-wrap items-center gap-2'>
                                    <strong className='whitespace-nowrap text-xs sm:text-sm'>
                                      {translate("Bill Number")}:
                                    </strong>
                                    <span className='text-sm font-semibold sm:text-base'>
                                      {bill?.number}
                                    </span>
                                  </div>
                                  <div className='mt-0 flex flex-wrap items-center gap-2'>
                                    <strong className='whitespace-nowrap text-xs sm:text-sm'>
                                      {translate("Accumulated from previous bills")}:
                                    </strong>
                                    <span className='text-sm font-semibold text-blue-500 sm:text-base'>
                                      {formatCurrency(
                                        getPreviouslyPaidTotal(bill, allBillsOnBoq),
                                        isHebrew
                                      )}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* <div className='hidden grid-cols-12 gap-2 border-b bg-muted/50 p-2 text-xs font-medium lg:grid'>
                              <div className='col-span-1'>#</div>
                              <div className='col-span-2'>{translate("Number")}</div>
                              <div className='col-span-1'>{translate("Company")}</div>
                              <div className='col-span-1'>{translate("Project")}</div>
                              <div className='col-span-1'>{translate("Contractor")}</div>
                              <div className='col-span-1'>{translate("Supervisor")}</div>
                              <div className='col-span-5 text-center'>{translate("Actions")}</div>
                            </div> */}
                            <BillRow
                              bill={bill}
                              revalidate={refetchReturnedBills}
                              status='new'
                              parentExpanded={!!expandedCards[bill?.id]}
                              allBills={returnedBills}
                              allBillsOnBoq={allBillsOnBoq}
                            />
                          </div>
                        )}
                      </div>
                    </Card>
                  </TooltipTrigger>
                  <TooltipContent
                    className={cn(
                      bill?.is_declined
                        ? "bg-red-500"
                        : bill?.is_signed
                          ? "bg-green-500"
                          : bill?.is_approved
                            ? "bg-primary"
                            : "bg-muted-foreground",
                    )}
                  >
                    {bill?.is_declined || bill?.is_signed ? (
                      <>
                        {translate(
                          bill?.is_declined ? "Bill Declined by Supervisor" : "Bill Signed",
                        )}
                      </>
                    ) : (
                      <>
                        {translate("Next Action")}:{" "}
                        {translate(
                          bill?.is_approved
                            ? "Requires Signing/Decline/Return"
                            : bill?.status === "new"
                              ? "Edit/Move to approve"
                              : "Requires Approval/Return",
                        )}
                      </>
                    )}
                  </TooltipContent>
                </Tooltip>
              ))}
          </div>

          {isLoadingReturnedBills && <PmsLoader />}
          {returnedBills.length < 1 && !isLoadingReturnedBills && (
            <div className='flex flex-col items-center justify-center py-12 text-center'>
              <div className='rounded-full bg-gray-100 p-4 mb-4'>
                <User className='h-8 w-8 text-gray-400' />
              </div>
              <p className='text-sm font-medium text-gray-900'>{translate("no bills returned for your review")}</p>
              <p className='mt-1 text-xs text-gray-500'>{translate("empty_contractor_hint")}</p>
            </div>
          )}
        </CardContent>
        <CardFooter className='flex justify-between'>
          <div className='text-sm text-muted-foreground'>
            {translate("Showing")} {returnedBills?.length} {translate("of")} {returnedBills?.length}{" "}
            {translate("items")}
          </div>
        </CardFooter>
      </Card>

      {showEditBillDialog && (
        <BoQEdit
          open={showEditBillDialog}
          onOpenChange={setEditBillDialog}
          boqId={boqId}
          type='contractor'
          currentBill={returnedBills.find((bill: any) => bill.id === selectBillId)}
        />
      )}
    </div>
  )
}
